/**
 * Notification fetch wrapper — Story 92.1
 *
 * Cross-tenant aggregation rule (ADR-020 §6): notification calls MUST NOT send
 * `X-Resource-Tenant-Id`. The shared `axiosInstance` and Orval `customFetch`
 * auto-inject that header and therefore cannot be used for the notification
 * domain. Every notification call in Stories 92.1–92.5 must go through this
 * wrapper or `openSseStream`.
 */

import { getApiGatewayUrl } from './apiGatewayUrl';
import { retryOn401 } from './reactiveRefresh';

const CSRF_COOKIE_NAME = 'xsrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

// Duplicated from axiosInstance.ts:58-72 by design (Story 92.1 Task 1):
// keep axiosInstance.ts untouched for blast radius; helper is small + stable.
function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }

  const [, value] = cookie.split('=');
  return decodeURIComponent(value);
}

export function buildNotificationHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!headers.has('X-Correlation-Id')) {
    headers.set('X-Correlation-Id', crypto.randomUUID());
  }
  const csrf = getCookie(CSRF_COOKIE_NAME);
  if (csrf && !headers.has(CSRF_HEADER_NAME)) {
    headers.set(CSRF_HEADER_NAME, csrf);
  }
  return headers;
}

export function getNotificationApiBaseUrl(): string {
  return getApiGatewayUrl();
}

/**
 * Cross-tenant `fetch` wrapper for the notification domain. Attaches CSRF +
 * Correlation-Id, sends cookies, and never attaches `X-Resource-Tenant-Id`.
 * `path` may be absolute (`http(s)://…`) or a Gateway-relative path
 * (`/api/notification?limit=1`).
 *
 * Wrapped in the shared reactive-refresh net (`retryOn401`): on a 401 from a
 * lapsed access cookie it runs the single-flight `refreshSession()` and replays
 * the request once. Headers are rebuilt on each attempt so the replay picks up
 * the rotated CSRF cookie. This is the notification domain's equivalent of the
 * axios/orval 401 interceptor, which it can't use (ADR-020 §6 forbids the
 * tenant header those clients inject).
 */
export async function notificationFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${getApiGatewayUrl()}${path}`;
  return retryOn401(
    () =>
      fetch(url, {
        ...init,
        headers: buildNotificationHeaders(init.headers),
        credentials: 'include',
      }),
    (response) => response.status
  );
}
